import React, { createContext, useContext, useEffect, useState } from 'react'

export type IMediaQueryContext = {
  isMobileDevice: boolean
  isTabletDevice: boolean
  isDesktopDevice: boolean
}

const defaults = {
  isMobileDevice: false,
  isTabletDevice: false,
  isDesktopDevice: true, // Default to true to avoid breaking existing implementations
}

export const MediaQueryContext = createContext<IMediaQueryContext>(defaults)

const IS_SERVER = typeof window === 'undefined'

const listenToMediaQuery = (
  query: MediaQueryList,
  listener: (event?: MediaQueryListEvent) => void
) => {
  if ('addEventListener' in query) {
    query.addEventListener('change', listener)

    return () => query.removeEventListener('change', listener)
  }

  query.addListener(listener)

  return () => query.removeListener(listener)
}

export const useMediaQuery = () => {
  const { isMobileDevice } = useContext(MediaQueryContext)

  return { isMobileDevice }
}

export const MediaQueryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isMobileDevice, setMobileDevice] = useState(defaults.isMobileDevice)
  const [isTabletDevice, setTabletDevice] = useState(defaults.isTabletDevice)
  const [isDesktopDevice, setDesktopDevice] = useState(defaults.isDesktopDevice)

  const addMetadata = (config: {
    mobile: boolean
    tablet: boolean
    desktop: boolean
  }) => {
    if (IS_SERVER) return

    const element = document.body

    if (element) {
      element.setAttribute('asktia__mobile', String(config.mobile))
      element.setAttribute('asktia__tablet', String(config.tablet))
      element.setAttribute('asktia__desktop', String(config.desktop))
    }
  }

  useEffect(() => {
    if (IS_SERVER) return

    if ('function' !== typeof window.matchMedia) return

    const mobileQuery = window.matchMedia('screen and (max-width: 47rem)')
    const tabletQuery = window.matchMedia(
      'screen and (min-width: 48rem) and (max-width: 63rem)'
    )
    const desktopQuery = window.matchMedia('screen and (min-width: 64rem)')

    const syncDeviceState = () => {
      const mobile = mobileQuery.matches
      const tablet = tabletQuery.matches
      const desktop = desktopQuery.matches

      setMobileDevice(mobile)
      setTabletDevice(tablet)
      setDesktopDevice(desktop)

      addMetadata({ mobile, tablet, desktop })
    }

    syncDeviceState()

    const removeMobileListener = listenToMediaQuery(mobileQuery, syncDeviceState)
    const removeTabletListener = listenToMediaQuery(tabletQuery, syncDeviceState)
    const removeDesktopListener = listenToMediaQuery(desktopQuery, syncDeviceState)

    return () => {
      removeMobileListener()
      removeTabletListener()
      removeDesktopListener()
    }
  }, [])

  return (
    <MediaQueryContext.Provider value={{ isMobileDevice, isTabletDevice, isDesktopDevice }}>
      {children}
    </MediaQueryContext.Provider>
  )
}

export default MediaQueryProvider
